import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import ResultsChart from './ResultsChart'
import { useAuth } from '~/routes/components/AuthProvider'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { Nullable } from '~/types'
import { Dropdown, Icon } from 'semantic-ui-react'
import dayjs from 'dayjs'
import EmisssionFilters from './EmissionFilters'
import { StyledPeriodSelect } from './style'

export enum Co2FiltersTypes {
  PERIOD = 'periodFrom',
  DATA_TYPES = 'type',
  FARMER_GROUP = 'farmerGroupId',
  FARMER = 'farmerId',
  DATA_COLLECTIONS = 'co2FootprintId',
  RACE = 'breedId',
  INDIVIDUAL_FILTERS = 'customFilterId'

}

const getCo2Filters = () => [
  {
    id: Co2FiltersTypes.PERIOD,
    placeholder: 'Period'
  },
  {
    id: Co2FiltersTypes.FARMER,
    placeholder: 'Search farmers'
  }
]

export type DataFilteredType = Nullable<{
  resultType: string
  periodFrom: string
  periodTo: string
  farmerId: string
  customFilters: Nullable<(string | number)[]>
  breeds: Nullable<(string | number)[]>
  perimeter: string | number
  administrativeDistrictId: Nullable<(string | number)[]>
}>

const Co2Climate: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const [co2Filters, setCo2Filters] = useState(getCo2Filters())
  const [checkedFilters, setCheckedFilters] = useState([co2Filters[0]])

  const [dataFiltered, setDataFiltered] = useState<DataFilteredType>(null)

  const {
    data: co2Settings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings
  } = ReportingQuery.getCo2ReportSettingsFar()

  const {
    data: co2Data,
    isLoading: isLoadingData,
    isFetching: isFetchingCo2Data,
    refetch: fetchCo2Data
  } = ReportingQuery.getCo2ReportData(dataFiltered)

  const {
    data: farmersList,
    isLoading: isLoadingFarmList
  } = CO2ClimateQuery.getCo2FarmersList(dataFiltered?.periodFrom ? { page: 0, search: '' } : null)

  useEffect(() => {
    if (co2Settings) {
      setDataFiltered({
        resultType: 'sold_milk_economic_allocation_fpcm_idf',
        periodFrom: co2Settings.periods?.[0]?.periodFrom || '',
        periodTo: co2Settings.periods?.[0]?.periodTo || '',
        farmerId: '',
        breeds: [co2Settings?.selectedData?.breedId],
        perimeter: co2Settings?.selectedData?.perimeter,
        customFilters: co2Settings?.selectedData?.customFilterId ? [co2Settings?.selectedData?.customFilterId] : [],
        administrativeDistrictId: co2Settings?.selectedData?.administrativeDistrictId ? [co2Settings?.selectedData?.administrativeDistrictId] : []
      })
    }
  }, [co2Settings])

  useEffect(() => {
    if (dataFiltered) {
      fetchCo2Data()
    }
  }, [dataFiltered])

  useEffect(() => {
    if (farmersList && farmersList?.length > 1) {
      const filters = getCo2Filters()
      const subFarmersFilters = []
      if (!co2Filters.find(filter => filter.id === Co2FiltersTypes.RACE)) {
        const breedFilter = filters.find(filter => filter.id === Co2FiltersTypes.RACE)
        subFarmersFilters.push(breedFilter)
      }
      if (!co2Filters.find(filter => filter.id === Co2FiltersTypes.FARMER)) {
        const farmerFilter = filters.find(filter => filter.id === Co2FiltersTypes.FARMER)
        subFarmersFilters.push(farmerFilter)
        setCheckedFilters([...checkedFilters, farmerFilter])
      }
      setCo2Filters([...co2Filters, ...subFarmersFilters])
    }
  }, [farmersList])

  useEffect(() => {
    refetchSettings()
  }, [t, roleSlug])

  useEffect(() => {
    const filters = localStorage.getItem('reportingCo2FiltersFarmer')
    if (filters) {
      const filtersParsed = JSON.parse(filters).filter(filter => filter.displayForRole.find(role => role === roleSlug))
      filtersParsed.forEach(filter => {
        const indexFilter = co2Filters.findIndex(farmerFilter => farmerFilter.id === filter.id)
        if (indexFilter >= 0) {
          filter.placeholder = co2Filters[indexFilter].placeholder
        }
      })
      setCheckedFilters(filtersParsed)
    }
  }, [])

  function renderChart() {
    return co2Data && <ResultsChart
      isLoading={isLoading || isFetchingCo2Data}
      data={co2Data}
      averageTHGLabel={co2Settings?.texts[0]?.translation}
      barColors={{
        resultsColor: co2Settings.co2FootprintSettings.yourValueColor,
        dairyResultsColor: co2Settings.co2FootprintSettings.dairyValueColor,
        defaultColor: co2Settings.co2FootprintSettings.bayernValueColor || '#8884d8'
      }}
      subSupplierSelected={Boolean(dataFiltered?.farmerId)}
      resultsUnit={[...co2Settings?.resultTypes[0], ...co2Settings?.resultTypes[1]].find(result => result.key === dataFiltered?.resultType)?.unit}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
    />
  }

  function renderPeriodFilter() {
    return <div style={{ display: 'flex', gap: 20 }}>
      <StyledPeriodSelect
        selectOnBlur={false}
        selection
        options={co2Settings?.periods?.map(period => ({
          text: `${dayjs(period.periodFrom).format('DD.MM.YYYY')} - ${dayjs(period.periodTo).format('DD.MM.YYYY')}`,
          value: `${period.periodFrom}/${period.periodTo}`
        }))}
        onChange={(_, { value }) => {
          const periods = value ? value.toString().split('/') : ['', '']
          setDataFiltered({
            ...dataFiltered,
            periodFrom: periods[0],
            periodTo: periods[1]
          })
        }}
        value={`${dataFiltered?.periodFrom}/${dataFiltered?.periodTo}`}
      />
      {co2Data?.dairyResults?.count < 10 && <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5
      }}>
        <Icon name='warning sign' color='yellow' style={{ fontSize: '1.4em' }} />
        <div>{'Keine Auswertung möglich, da zu wenig Vergleichsdatensätze vorhanden. Bitte passen sie die Filtereinstellungen an.'}</div>
      </div>}
    </div>
  }

  function renderSubFarmers() {
    return <Dropdown
      options={co2Settings?.subFarmers?.map(subFarmer => ({
        text: `${subFarmer.companyName} ${subFarmer?.supplierNo ? `(${subFarmer?.supplierNo})` : ''}`,
        value: subFarmer.id
      }))}
      style={{ width: 300 }}
      value={dataFiltered.farmerId}
      onChange={(_, { value }) => setDataFiltered({
        ...dataFiltered,
        farmerId: value as string
      })}
      selectOnNavigation={false}
      selection
      clearable
      placeholder={t('Select farmer')}
      selectOnBlur={false}
    />
  }

  function renderNoDairyResultsMessage() {
    return <div style={{ fontStyle: 'italic' }}>
      {'Keine Auswertung möglich, da zu wenige Landwirte den Klima-Check ausgefüllt haben.'}
    </div>
  }

  const isLoading = isLoadingData || isLoadingSettings || isLoadingFarmList

  return <Layout
    title={t('Data collections')}
    segmented
    isBusy={isLoading}
    actionAlignToTop
    filters={renderPeriodFilter()}
    actions={co2Settings?.subFarmers?.length > 1 && dataFiltered && renderSubFarmers()}
    isCo2Loader
  >
    <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'self-start', marginTop: '1em' }}>
      <div style={{ width: '55%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {!isLoading && renderChart()}
        {!isLoading && co2Data?.dairyResults?.count < 10 && renderNoDairyResultsMessage()}
      </div>
      <EmisssionFilters
        dataFiltered={dataFiltered}
        handleChangeDataFiltered={(data) => setDataFiltered(data)}
        isLoading={isLoading || isFetchingCo2Data}
        co2Settings={co2Settings}
      />
    </div>
  </Layout>
}
export default Co2Climate